import { memoize, get } from 'lodash';

import { toId } from '../../helpers';

export const ACTION_CREATE = 'create';
export const ACTION_DELETE = 'delete';
export const ACTION_VIEW = 'view';
export const ACTION_EDIT = 'edit';
export const ACTION_ANY = '?';
export const ACTION_EVERY = '*';
export const ACTION_ALL = [ACTION_CREATE, ACTION_VIEW, ACTION_EDIT, ACTION_DELETE];

export const ACTION_IDS = [ACTION_CREATE, ACTION_VIEW, ACTION_EDIT, ACTION_DELETE, ACTION_EVERY, ACTION_ANY];

const actionsSet = new Set(ACTION_IDS);

/**
 * @param {string} permission Permission string to parse
 * @returns {{resource: string, actions: string[]}} Parsed resource and actions list
 */
export const parse = memoize((permission) => {
    const [resource, rawActions = ACTION_EVERY] = permission.split(':');
    const actions = new Set();

    if (rawActions) {
        for (const action of rawActions.split(',')) {
            if (action === ACTION_EVERY) {
                for (const name of ACTION_ALL) {
                    actions.add(name);
                }
            } else if (actionsSet.has(action)) {
                actions.add(action);
            } else {
                throw new Error(`Unknown action "${action}"`);
            }
        }
    } else {
        for (const name of ACTION_ALL) {
            actions.add(name);
        }
    }

    return {
        resource,
        actions: [...actions],
    };
});

export class AccessPermissionRequest {
    constructor(context, subject, role, resourceOrPermission, actions) {
        this.context = context;
        this.workspace = context.workspace || context;
        this.subject = subject;
        this.role = role || this.workspace?.billing?.plan;

        if (actions) {
            this.resource = resourceOrPermission;
            this.actions = actions;
        } else {
            this.fill(resourceOrPermission);
        }
    }

    get description() {
        return `[@${this.role} ${this.resource}${this.isEvery() ? '' : ':' + this.actions.join(',')}]${
            this.subject ? ' @ ' + JSON.stringify(this.subject) : ''
        }`;
    }

    isBy(user) {
        return toId(this.workspace?.owner) === toId(user);
    }

    get(path, def) {
        return get(this.context, path, def);
    }

    /**
     * @param {string} permission Permission string to parse
     * @returns {this} `this`
     */
    fill(permission) {
        return Object.assign(this, parse(permission));
    }

    /**
     * @param {string} permission Permission string to parse
     * @returns {boolean} `True` if this request matches `permission`
     */
    match(permission) {
        const { resource, actions } = parse(permission);

        return this.on(resource) && this.isEvery(actions);
    }

    /**
     * @param {string[]} actions Actions list
     * @returns {AccessPermissionRequest} Copy of this request, but matching only provided `actions`
     */
    only(actions) {
        return new AccessPermissionRequest(this.workspace, this.subject, this.role, this.resource, actions);
    }

    on(resource) {
        return this.resource === resource;
    }

    includes(action) {
        return this.actions.includes(action);
    }

    is(action) {
        return this.actions.length === 1 && this.actions[0] === action;
    }

    isAny() {
        return this.actions.includes(ACTION_ANY);
    }

    isEvery(actions = ACTION_ALL) {
        return actions.every((action) => this.actions.includes(action));
    }
}
